import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react"

import { ANIMATION_DURATION, ANIMATION_EASING } from "@vc/constants/animations"
import { cn } from "@vc/lib/utils"
import type { TileRect } from "./tile-geometry"
import "./TileLayer.css"

export type TileSlot = {
  /** Identity of what is drawn here. Stable for as long as the tile is on screen. */
  key: string
  rect: TileRect
  content: ReactNode
  className?: string
  title?: string
  /** Makes the slot a button — the rails pin on click. */
  onClick?: () => void
}

/**
 * Whether a click on a slot button was a click on the tile, rather than on one
 * of the tile's own controls.
 *
 * The rails pin whoever is clicked, and they toggle. A click on the tile's
 * "More options" button or fullscreen button bubbled up and toggled the pin
 * too, and so did a click in the tile's menu: it renders in a portal, outside
 * this element, but React still bubbles it here. So "Pin for all" pinned the
 * tile and this click unpinned it again on the host's own screen — invisible
 * while the presentation held the stage, and the host alone was left unpinned
 * once it stopped.
 */
function isClickOnTileItself(slot: Element, target: EventTarget | null): boolean {
  if (!(target instanceof Element) || !slot.contains(target)) return false
  const control = target.closest('button, a, input, select, textarea, [role="menuitem"], [role="menu"]')
  return control === null || control === slot
}

type TileLayerProps = {
  slots: readonly TileSlot[]
  /** The box the rectangles were computed for. */
  width: number
  height: number
  /**
   * Animate the next re-layout even though the box itself changed size.
   *
   * By default a change of box size applies instantly: while a side panel
   * opens or the rail is dragged the tiles must track the edge frame by frame,
   * and a transition would make them trail behind it. The exception is an
   * orientation change, which settles once and reads better as a glide.
   */
  animateResize?: boolean
  className?: string
}

const MOVE_TRANSITION = ["transform", "width", "height"]
  .map((prop) => `${prop} ${ANIMATION_DURATION.TILE_MOVE}ms ${ANIMATION_EASING.EASE_OUT}`)
  .join(", ")

/**
 * Tiles as siblings of one container, each placed by its rectangle.
 *
 * This is what keeps a participant's tile alive while the layout changes
 * around it. Every slot is keyed by identity and lives directly in this
 * container, so when someone joins and the rows are redistributed, existing
 * tiles are the SAME elements given new coordinates — nothing is unmounted, no
 * `<video>` is re-created, no stream is re-attached.
 *
 * Movement is a CSS transition on `transform`/`width`/`height`. The browser
 * retargets a transition from wherever the tile currently is, so a second
 * layout change arriving mid-glide (a second join, a camera coming on) bends
 * the motion instead of jumping. No measuring, no requestAnimationFrame, and
 * no React renders while a tile is moving.
 *
 * Tiles that appear after the first layout — a new participant, the next page
 * — fade in. Tiles present when the layer first renders do not: a layout
 * switch or a fresh grid is not an arrival.
 */
export function TileLayer({
  slots,
  width,
  height,
  animateResize = false,
  className,
}: TileLayerProps) {
  // Box size of the previous committed layout, to tell a resize from a
  // change in who is on screen. Read during render, written after commit.
  const committedBoxRef = useRef<{ width: number; height: number } | null>(null)
  const prevBox = committedBoxRef.current
  const boxChanged = prevBox !== null && (prevBox.width !== width || prevBox.height !== height)
  // Transition values are applied in the same style change as the new
  // geometry, so `none` here makes a resize land instantly while a membership
  // change in a steady box animates. Nothing needs restoring afterwards: the
  // next commit carries its own value.
  const transition = boxChanged && !animateResize ? "none" : MOVE_TRANSITION

  // Keys on screen at the last commit, and which of them arrived after the
  // first layout. An arrival keeps its entrance marker for as long as it stays
  // mounted — dropping the attribute mid-animation would cut the fade short.
  // Null until the first commit that actually has tiles: the pass before the
  // container is measured draws nothing and must not make the real first
  // layout look like a room full of arrivals.
  const knownKeysRef = useRef<Set<string> | null>(null)
  const enteringRef = useRef<Set<string>>(new Set())
  const isFirstLayout = knownKeysRef.current === null
  const isEntering = (key: string) =>
    enteringRef.current.has(key) || (!isFirstLayout && !knownKeysRef.current!.has(key))

  useLayoutEffect(() => {
    committedBoxRef.current = { width, height }
    if (knownKeysRef.current === null && slots.length === 0) return
    const current = new Set(slots.map((s) => s.key))
    const firstLayout = knownKeysRef.current === null
    const entering = new Set<string>()
    for (const key of current) {
      if (enteringRef.current.has(key) || (!firstLayout && !knownKeysRef.current!.has(key))) {
        entering.add(key)
      }
    }
    enteringRef.current = entering
    knownKeysRef.current = current
  })

  return (
    <div className={cn("tile-layer", className)}>
      {slots.map((slot) => {
        const { x, y, w, h } = slot.rect
        const style: CSSProperties = {
          width: w,
          height: h,
          transform: `translate3d(${x}px, ${y}px, 0)`,
          transition,
        }
        const content = (
          <div className="tile-slot-content" data-enter={isEntering(slot.key) ? "" : undefined}>
            {slot.content}
          </div>
        )
        return slot.onClick ? (
          <button
            key={slot.key}
            type="button"
            onClick={(e) => {
              if (isClickOnTileItself(e.currentTarget, e.target)) slot.onClick?.()
            }}
            title={slot.title}
            className={cn("tile-slot", slot.className)}
            style={style}
          >
            {content}
          </button>
        ) : (
          <div key={slot.key} title={slot.title} className={cn("tile-slot", slot.className)} style={style}>
            {content}
          </div>
        )
      })}
    </div>
  )
}
